import React from "react";
import {
  Box,
  Typography,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

interface FAQ {
  id: string;
  question: string;
  answer: string;
}

const FAQS: FAQ[] = [
  {
    id: "panel1",
    question: "How do I book a pet sitter?",
    answer:
      "Search for sitters in your area using our map and filters. Click on a sitter's profile to view their details, then select your dates and click 'Book Now'. The sitter will receive your request and respond within 24 hours.",
  },
  {
    id: "panel2",
    question: "How do I add my pet to my profile?",
    answer:
      "Go to 'My Pets' from the menu and click 'Add New Pet'. Fill in your pet's details including name, breed, age, and any special care instructions. You can add photos to help sitters get to know your furry friend!",
  },
  {
    id: "panel3",
    question: "What payment methods do you accept?",
    answer:
      "We accept all major credit cards (Visa, Mastercard, American Express, Discover) and debit cards. You can save multiple payment methods in your profile for faster booking.",
  },
  {
    id: "panel4",
    question: "When will I be charged for a booking?",
    answer:
      "Your card is authorized when you book, but you're only charged after the sitter accepts your request. If the sitter declines or doesn't respond within 24 hours, the authorization is released.",
  },
  {
    id: "panel5",
    question: "What's your cancellation policy?",
    answer:
      "Free cancellation up to 24 hours before the booking start time. Cancellations within 24 hours are subject to a 50% charge. No refunds for cancellations after the booking has started. Emergency exceptions may apply.",
  },
  {
    id: "panel6",
    question: "How do I contact a sitter?",
    answer:
      "Once you've sent a booking request, you can message the sitter directly through our in-app messaging system. Go to 'Messages' to view all your conversations.",
  },
  {
    id: "panel7",
    question: "What if I need to change my booking dates?",
    answer:
      "Go to 'My Bookings', select the booking, and click 'Request Changes'. The sitter will be notified and can accept or decline the modification. Changes are subject to sitter availability.",
  },
  {
    id: "panel8",
    question: "Are sitters background checked?",
    answer:
      "We encourage all sitters to complete background checks, which are indicated by a verification badge on their profile. You can filter search results to show only verified sitters.",
  },
  {
    id: "panel9",
    question: "What if my pet has special needs or medications?",
    answer:
      "You can add detailed special care instructions and medication schedules to your pet's profile. Make sure to discuss these with the sitter before confirming your booking.",
  },
  {
    id: "panel10",
    question: "How do reviews work?",
    answer:
      "After your booking is completed, you'll receive a prompt to review the sitter. Reviews are public and help other pet parents make informed decisions. Sitters can also review clients.",
  },
  {
    id: "panel11",
    question: "What if there's an emergency during my booking?",
    answer:
      "Your sitter has access to your emergency contact information and can reach out to them if needed. For urgent situations, sitters are instructed to contact local veterinary services. You'll be notified immediately.",
  },
  {
    id: "panel12",
    question: "How do I get a refund?",
    answer:
      "Refunds are automatically processed according to our cancellation policy. For special circumstances, contact our support team at support@happypaws.com. Refunds typically take 5-7 business days to appear on your statement.",
  },
];

interface FAQSectionProps {
  expandedFaq: string | false;
  onFaqChange: (
    panel: string
  ) => (event: React.SyntheticEvent, isExpanded: boolean) => void;
}

const FAQSection: React.FC<FAQSectionProps> = ({ expandedFaq, onFaqChange }) => {
  return (
    <Box id="faq-section" sx={{ mt: 6 }}>
      <Typography variant="h5" fontWeight={600} mb={3}>
        Frequently Asked Questions
      </Typography>
      <Paper sx={{ borderRadius: 2, overflow: "hidden", border: "1px solid #E0E0E0" }}>
        {FAQS.map((faq, index) => (
          <Accordion
            key={faq.id}
            expanded={expandedFaq === faq.id}
            onChange={onFaqChange(faq.id)}
            disableGutters
            elevation={0}
            sx={{
              borderBottom: index < FAQS.length - 1 ? "1px solid #E0E0E0" : "none",
              "&:before": { display: "none" },
              "&.Mui-expanded": {
                backgroundColor: "rgba(23, 139, 142, 0.04)",
              },
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon sx={{ color: "#178B8E" }} />}
              sx={{
                px: 3,
                py: 0.5,
                "&.Mui-expanded": {
                  borderBottom: "1px solid rgba(23, 139, 142, 0.2)",
                },
              }}
            >
              <Typography variant="subtitle1" fontWeight={500}>
                {faq.question}
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ px: 3, py: 2 }}>
              <Typography variant="body2" color="text.secondary" lineHeight={1.7}>
                {faq.answer}
              </Typography>
            </AccordionDetails>
          </Accordion>
        ))}
      </Paper>
    </Box>
  );
};

export default FAQSection;
